import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Page,
  H1,
  P,
  Button,
  TextField,
  Box,
  Card,
  CardContent,
  Stack,
  Divider,
  Alert,
} from '../components';
import { Google, Apple, ArrowBack } from '@mui/icons-material';

const GuardianLoginPage = () => {
  const navigate = useNavigate();
  const {
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signInWithApple,
  } = useAuth();

  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (isSignUp && password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const { error } = isSignUp
        ? await signUpWithEmail(email, password)
        : await signInWithEmail(email, password);

      if (error) {
        setError(error.message);
      } else {
        navigate('/guardian');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setError('');
    setLoading(true);

    try {
      const { error } = await signInWithGoogle();
      if (error) {
        setError(error.message);
      }
      // Navigation will be handled by the auth state change
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAppleAuth = async () => {
    setError('');
    setLoading(true);

    try {
      const { error } = await signInWithApple();
      if (error) {
        setError(error.message);
      }
      // Navigation will be handled by the auth state change
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page maxWidth="sm" fullHeight>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="80vh"
        gap={3}
      >
        <Button
          component={RouterLink}
          to="/login"
          startIcon={<ArrowBack />}
          sx={{ alignSelf: 'flex-start', mb: 2 }}
        >
          Back to Login Options
        </Button>

        <H1>{isSignUp ? 'Create Guardian Account' : 'Guardian Login'}</H1>
        <P color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
          {isSignUp
            ? "Create your account to manage your children's learning"
            : 'Welcome back! Sign in to your guardian account'}
        </P>

        <Card elevation={2} sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Social Auth Buttons */}
            <Stack spacing={2} sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<Google />}
                onClick={handleGoogleAuth}
                disabled={loading}
              >
                Continue with Google
              </Button>

              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<Apple />}
                onClick={handleAppleAuth}
                disabled={loading}
              >
                Continue with Apple
              </Button>
            </Stack>

            <Divider sx={{ my: 3 }}>
              <P variant="body2" color="text.secondary">
                or
              </P>
            </Divider>

            {/* Email/Password Form */}
            <Box component="form" onSubmit={handleEmailAuth}>
              <Stack spacing={2}>
                <TextField
                  type="email"
                  label="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  disabled={loading}
                />

                <TextField
                  type="password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  disabled={loading}
                />

                {isSignUp && (
                  <TextField
                    type="password"
                    label="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    fullWidth
                    disabled={loading}
                  />
                )}

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading
                    ? 'Please wait...'
                    : isSignUp
                      ? 'Create Account'
                      : 'Sign In'}
                </Button>
              </Stack>
            </Box>

            <Box textAlign="center" sx={{ mt: 3 }}>
              <Button
                variant="text"
                onClick={() => setIsSignUp(!isSignUp)}
                disabled={loading}
              >
                {isSignUp
                  ? 'Already have an account? Sign In'
                  : 'Need an account? Sign Up'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Page>
  );
};

export default GuardianLoginPage;
